import { useState, useEffect, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, QrCode, Milk, Leaf, Plus, Camera, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import FloatingCart from "@/components/cart/FloatingCart";
import LocationSelectorModal from "@/components/cart/LocationSelectorModal";
import EscaparateBanner from "@/components/cart/EscaparateBanner";
import ProductDetailModal from "@/components/cart/ProductDetailModal";
import { useCart } from "@/contexts/CartContext";
import { PRODUCT_CATALOG, getProductById } from "@/constants/products";
import { supabase } from "@/integrations/supabase/client";
import type { ProductDefinition, SelectedCustomization, LOCATIONS } from "@/types/orders";

interface ProductItem {
  id: string;
  name: string;
  price: number;
  priceDisplay: string;
  variant?: string;
  tags?: ("sin-lactosa" | "vegetariano" | "con-lactosa")[];
}

interface SubCategory {
  name: string;
  items: ProductItem[];
  note?: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  subcategories: SubCategory[];
}

const TagBadge = ({ tag }: { tag: string }) => {
  switch (tag) {
    case "sin-lactosa":
      return (
        <Badge variant="outline" className="text-xs border-green-600 text-green-700 bg-green-50">
          <Milk className="h-3 w-3 mr-1" />
          Sin Lactosa
        </Badge>
      );
    case "vegetariano":
      return (
        <Badge variant="outline" className="text-xs border-emerald-600 text-emerald-700 bg-emerald-50">
          <Leaf className="h-3 w-3 mr-1" />
          Vegetariano
        </Badge>
      );
    case "con-lactosa":
      return (
        <Badge variant="outline" className="text-xs border-amber-600 text-amber-700 bg-amber-50">
          <Milk className="h-3 w-3 mr-1" />
          Con Lactosa
        </Badge>
      );
    default:
      return null;
  }
};

const Carta = () => {
  const [activeCategory, setActiveCategory] = useState("bebidas-frias");
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductDefinition | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unavailableProducts, setUnavailableProducts] = useState<Set<string>>(new Set());
  const { orderType, tableNumber, locationCode, sessionMode, isValidSession, items, addItem, removeItem } = useCart();

  // Normalize location code to match database format
  const normalizeLocationCode = (code: string | null): string => {
    if (!code) return "siete-palmas"; // Default to Siete Palmas
    
    // Legacy code mappings
    const legacyMappings: Record<string, string> = {
      "felo-monzon": "siete-palmas",
      "felo_monzon": "siete-palmas",
      "jordan": "lomo-frailes",
      "siete_palmas": "siete-palmas",
      "lomo_frailes": "lomo-frailes",
    };
    
    const normalized = code.toLowerCase().trim().replace(/_/g, "-").replace(/\s+/g, "-");
    return legacyMappings[normalized] || normalized;
  };

  // Check if user is browsing from store context (QR scan) or from home
  const isInStoreContext = !!locationCode;

  // Fetch product availability for the current location - ONLY when in store context
  useEffect(() => {
    // If user is browsing from home (no store param), skip availability check
    // Show all products as available
    if (!isInStoreContext) {
      console.log("[CARTA] Modo Escaparate (desde casa) - mostrando todos los productos disponibles");
      setUnavailableProducts(new Set());
      return;
    }

    const fetchAvailability = async () => {
      const normalizedCode = normalizeLocationCode(locationCode);
      
      // First we need to get the location UUID from the code
      const { data: locationData } = await supabase
        .from("locations")
        .select("id")
        .eq("code", normalizedCode)
        .maybeSingle();
      
      if (!locationData?.id) {
        console.log("[CARTA] Location not found for code:", normalizedCode);
        setUnavailableProducts(new Set()); // Clear unavailable - assume all available
        return;
      }
      
      console.log("[CARTA] Modo Tienda - verificando stock para:", normalizedCode, locationData.id);
      
      // Now fetch unavailable products for this location
      const { data: availabilityData, error } = await supabase
        .from("product_availability")
        .select("product_id, is_available")
        .eq("location_id", locationData.id)
        .eq("is_available", false);
      
      if (error) {
        console.error("[CARTA] Error fetching availability:", error);
        return;
      }
      
      if (availabilityData) {
        const unavailableSet = new Set(availabilityData.map(item => item.product_id));
        console.log("[CARTA] Productos agotados:", unavailableSet.size);
        setUnavailableProducts(unavailableSet);
      }
    };
    
    fetchAvailability();
    
    // Subscribe to realtime changes - only when in store context
    if (!isInStoreContext) return;

    const normalizedCode = normalizeLocationCode(locationCode);
    
    const setupRealtimeSubscription = async () => {
      // Get location ID for filtering
      const { data: locationData } = await supabase
        .from("locations")
        .select("id")
        .eq("code", normalizedCode)
        .maybeSingle();
      
      if (!locationData?.id) return null;
      
      const channel = supabase
        .channel(`product_availability_${normalizedCode}`)
        .on(
          "postgres_changes",
          { 
            event: "*", 
            schema: "public", 
            table: "product_availability",
            filter: `location_id=eq.${locationData.id}`
          },
          (payload) => {
            console.log("[CARTA] Realtime availability update:", payload);
            // Refetch on any change - need to define fetchAvailability again
            const refetch = async () => {
              const { data: availabilityData } = await supabase
                .from("product_availability")
                .select("product_id, is_available")
                .eq("location_id", locationData.id)
                .eq("is_available", false);
              
              if (availabilityData) {
                setUnavailableProducts(new Set(availabilityData.map(item => item.product_id)));
              }
            };
            refetch();
          }
        )
        .subscribe();
      
      return channel;
    };
    
    let channel: ReturnType<typeof supabase.channel> | null = null;
    setupRealtimeSubscription().then(ch => { channel = ch; });
    
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [locationCode, isInStoreContext]);

  // Check if a product is available
  const isProductAvailable = (productId: string): boolean => {
    return !unavailableProducts.has(productId);
  };

  // Show location modal on first visit if no order type set via QR
  // BUT only if we have a valid session (not in escaparate mode)
  useEffect(() => {
    if (!orderType && !locationCode && isValidSession) {
      const timer = setTimeout(() => setShowLocationModal(true), 500);
      return () => clearTimeout(timer);
    }
  }, [orderType, locationCode, isValidSession]);

  // Determine if add-to-cart should be disabled (Modo Escaparate)
  const isEscaparateMode = sessionMode === "escaparate" && !isValidSession;

  // Get existing quantity in cart for a product
  const getProductQuantityInCart = (productId: string | undefined): number => {
    if (!productId) return 0;
    const cartItem = items.find(item => item.id === productId);
    return cartItem?.quantity || 0;
  };

  // Get existing options in cart for a product
  const getProductOptionsInCart = (productId: string | undefined): SelectedCustomization[] => {
    if (!productId) return [];
    const cartItem = items.find(item => item.id === productId);
    return cartItem?.selectedExtras || [];
  };

  // Handle product click to open modal
  const handleProductClick = (productId: string) => {
    // Block if escaparate mode or product is unavailable
    if (isEscaparateMode) return;
    if (!isProductAvailable(productId)) return;
    
    const catalogProduct = getProductById(productId);
    if (catalogProduct) {
      setSelectedProduct(catalogProduct);
      setIsModalOpen(true);
    }
  };

  // Handle add to cart from modal
  const handleAddFromModal = (qty: number, selectedOptions: SelectedCustomization[]) => {
    if (!selectedProduct) return;
    
    const extrasTotal = selectedOptions.reduce((sum, opt) => sum + opt.price, 0);
    const unitPrice = selectedProduct.price + extrasTotal;
    
    // Add items one by one to match addItem signature
    for (let i = 0; i < qty; i++) {
      addItem({
        id: selectedProduct.id,
        name: selectedProduct.name,
        category: selectedProduct.category || "",
        price: unitPrice,
        selectedExtras: selectedOptions,
        basePrice: selectedProduct.price,
      });
    }
  };

  // Handle remove from cart
  const handleRemoveFromCart = (productId: string) => {
    removeItem(productId);
  };

  const categories: Category[] = [
    {
      id: "bebidas-frias",
      name: "🥤 Bebidas Frías",
      icon: "🥤",
      subcategories: [
        {
          name: "Refrescos y Zumos",
          items: [
            { id: "zumo", name: "Zumo", price: 1.60, priceDisplay: "1,60€" },
            { id: "refresco", name: "Refresco", price: 1.60, priceDisplay: "1,60€" },
            { id: "nestea", name: "Nestea", price: 1.70, priceDisplay: "1,70€" },
            { id: "agua", name: "Agua", price: 0.60, priceDisplay: "0,60€" },
          ],
        },
        {
          name: "Zumo de Naranja Natural",
          items: [
            { id: "zumo-naranja-peq", name: "Zumo de Naranja Pequeño", price: 2.80, priceDisplay: "2,80€" },
            { id: "zumo-naranja-gde", name: "Zumo de Naranja Grande", price: 3.50, priceDisplay: "3,50€" },
          ],
        },
        {
          name: "Cafés Fríos",
          items: [
            { id: "cafe-helado", name: "Café Helado", price: 2.80, priceDisplay: "2,80€", tags: ["con-lactosa"] },
          ],
        },
      ],
    },
    {
      id: "bebidas-calientes",
      name: "☕ Bebidas Calientes",
      icon: "☕",
      subcategories: [
        {
          name: "Cafés Tradicionales",
          items: [
            { id: "cafe-solo", name: "Café Solo", price: 1.10, priceDisplay: "1,10€" },
            { id: "cortado-corto", name: "Cortado Corto", price: 1.20, priceDisplay: "1,20€", tags: ["con-lactosa"] },
            { id: "cafe-con-leche", name: "Café con Leche", price: 1.60, priceDisplay: "1,60€", tags: ["con-lactosa"] },
            { id: "cafe-solo-maquina", name: "Café Solo Máquina", price: 1.30, priceDisplay: "1,30€" },
            { id: "capuchino", name: "Capuchino", price: 1.65, priceDisplay: "1,65€", tags: ["con-lactosa"] },
          ],
        },
        {
          name: "Condensado",
          items: [
            { id: "condensado-corto", name: "Condensado Corto", price: 1.25, priceDisplay: "1,25€", tags: ["con-lactosa"] },
            { id: "condensado-largo", name: "Condensado Largo", price: 1.65, priceDisplay: "1,65€", tags: ["con-lactosa"] },
          ],
        },
        {
          name: "Descafeinado",
          items: [
            { id: "descafeinado-corto", name: "Descafeinado Corto", price: 1.30, priceDisplay: "1,30€", tags: ["con-lactosa"] },
            { id: "descafeinado-largo", name: "Descafeinado Largo", price: 1.65, priceDisplay: "1,65€", tags: ["con-lactosa"] },
          ],
        },
        {
          name: "Opciones Sin Lactosa",
          items: [
            { id: "cafe-leche-sinlactosa", name: "Café con Leche sin Lactosa / Avena / Soja", price: 1.65, priceDisplay: "1,65€", tags: ["sin-lactosa"] },
            { id: "cortado-sinlactosa", name: "Cortado Corto sin Lactosa / Avena / Soja", price: 1.25, priceDisplay: "1,25€", tags: ["sin-lactosa"] },
          ],
        },
        {
          name: "Chocolates e Infusiones",
          items: [
            { id: "infusiones", name: "Infusiones Especiales", price: 1.50, priceDisplay: "1,50€", tags: ["vegetariano"] },
            { id: "colacao", name: "ColaCao / Chocolate Caliente", price: 1.50, priceDisplay: "1,50€", tags: ["con-lactosa"] },
            { id: "taza-chocolate", name: "Taza de Chocolate", price: 1.60, priceDisplay: "1,60€", tags: ["con-lactosa"] },
          ],
        },
      ],
    },
    {
      id: "salado",
      name: "🥐 Salado",
      icon: "🥐",
      subcategories: [
        {
          name: "Croissants",
          items: [
            { id: "croissant-normal", name: "Croissant Normal", price: 1.20, priceDisplay: "1,20€", tags: ["vegetariano"] },
            { id: "croissant-vegetal", name: "Croissant Vegetal", price: 2.10, priceDisplay: "2,10€", tags: ["vegetariano"] },
            { id: "croissant-mixto", name: "Croissant Mixto", price: 2.10, priceDisplay: "2,10€" },
            { id: "croissant-serrano", name: "Croissant Serrano", price: 2.80, priceDisplay: "2,80€" },
            { id: "croissant-vegetal-mixto", name: "Croissant Vegetal Mixto", price: 2.70, priceDisplay: "2,70€" },
          ],
        },
        {
          name: "Caracolas",
          items: [
            { id: "caracola-atun", name: "Caracola Vegetal Atún", price: 2.30, priceDisplay: "2,30€" },
            { id: "caracola-pavo", name: "Caracola Vegetal Pavo", price: 2.80, priceDisplay: "2,80€" },
            { id: "caracola-atun-mixto", name: "Caracola Vegetal Atún Mixto", price: 2.90, priceDisplay: "2,90€" },
          ],
        },
        {
          name: "Sándwiches y Bocadillos",
          items: [
            { id: "sandwich-mixto", name: "Sándwich Mixto", price: 2.00, priceDisplay: "2,00€" },
            { id: "pulguita-pollo", name: "Pulguita de Pollo", price: 2.80, priceDisplay: "2,80€" },
            { id: "sandwich-atun-queso", name: "Sándwich de Atún y Queso Fundido", price: 2.80, priceDisplay: "2,80€" },
          ],
        },
      ],
    },
    {
      id: "menus",
      name: "🍽️ Menús",
      icon: "🍽️",
      subcategories: [
        {
          name: "Menús Clásicos",
          note: "Todos los menús incluyen bebida caliente y zumo de naranja pequeño",
          items: [
            { id: "menu-croissant-vegetal", name: "Menú Croissant Vegetal", price: 5.00, priceDisplay: "5,00€", tags: ["vegetariano"] },
            { id: "menu-sandwich-mixto", name: "Menú Sándwich Mixto", price: 5.00, priceDisplay: "5,00€" },
            { id: "menu-croissant-nutella", name: "Menú Croissant Nutella", price: 5.00, priceDisplay: "5,00€", tags: ["vegetariano"] },
            { id: "menu-croissant-mixto", name: "Menú Croissant Mixto", price: 5.30, priceDisplay: "5,30€" },
            { id: "menu-caracola-atun", name: "Menú Caracola Atún", price: 5.30, priceDisplay: "5,30€" },
            { id: "menu-croissant-serrano", name: "Menú Croissant Serrano", price: 5.30, priceDisplay: "5,30€" },
            { id: "menu-croissant-vegetal-mixto", name: "Menú Croissant Vegetal Mixto", price: 5.30, priceDisplay: "5,30€" },
          ],
        },
        {
          name: "⭐ Menús Especiales",
          note: "Todos los menús incluyen bebida caliente y zumo de naranja pequeño",
          items: [
            { id: "menu-sandwich-atun", name: "Menú Sándwich Atún y Queso Fundido", price: 5.90, priceDisplay: "5,90€" },
            { id: "menu-pulguita", name: "Menú Pulguita Pollo", price: 5.90, priceDisplay: "5,90€" },
            { id: "menu-caracola-pavo", name: "Menú Caracola Pavo", price: 5.90, priceDisplay: "5,90€" },
            { id: "menu-caracola-atun-mixta", name: "Menú Caracola Atún Mixta", price: 5.90, priceDisplay: "5,90€" },
          ],
        },
      ],
    },
  ];

  // Generate QR URL with current location if available
  const currentUrl = typeof window !== "undefined" 
    ? `${window.location.origin}/carta${locationCode ? `?store=${locationCode}` : ""}`
    : "https://pasteleriareyes.es/carta";

  return (
    <Layout>
      {/* Escaparate Banner - Only show in View Only mode */}
      {isEscaparateMode && <EscaparateBanner variant="header" />}

      {/* Order Type Indicator - Only show when valid session */}
      {orderType && isValidSession && (
        <div className={`py-2 px-4 text-center text-sm font-medium ${
          orderType === "mesa" 
            ? "bg-green-100 text-green-800 border-b border-green-200" 
            : orderType === "llevar"
              ? "bg-blue-100 text-blue-800 border-b border-blue-200"
              : "bg-orange-100 text-orange-800 border-b border-orange-200"
        }`}>
          {orderType === "mesa" ? (
            <>🍽️ Pedido en Local - Mesa {tableNumber}</>
          ) : orderType === "llevar" ? (
            <>🛍️ Para Recoger (Takeaway)</>
          ) : (
            <>📦 Encargo Especial</>
          )}
          <button
            onClick={() => setShowLocationModal(true)}
            className="ml-2 underline opacity-80 hover:opacity-100"
          >
            Cambiar
          </button>
        </div>
      )}

      {/* Hero */}
      <section className="bg-card py-8 md:py-12 border-b border-border">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-serif text-3xl md:text-5xl font-bold text-primary mb-3">
            Nuestra Carta
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6 text-sm md:text-base">
            Productos artesanales elaborados cada día en nuestro obrador.
          </p>

          {/* QR Code Section - Hidden on mobile */}
          <Card className="hidden md:inline-block bg-background border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4 justify-center">
                <Smartphone className="h-5 w-5 text-secondary" />
                <span className="text-sm font-medium text-primary">
                  Escanea para ver en tu móvil
                </span>
              </div>
              <div className="bg-card p-4 rounded-lg inline-block">
                <QRCodeSVG
                  value={currentUrl}
                  size={100}
                  level="H"
                  includeMargin={false}
                  fgColor="hsl(25, 45%, 18%)"
                  bgColor="transparent"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-3 flex items-center justify-center gap-1">
                <QrCode className="h-3 w-3" />
                Acceso rápido desde las mesas
              </p>
            </CardContent>
          </Card>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            <TagBadge tag="sin-lactosa" />
            <TagBadge tag="con-lactosa" />
            <TagBadge tag="vegetariano" />
          </div>
        </div>
      </section>

      {/* Menu */}
      <section className="py-6 md:py-10 bg-background pb-32 md:pb-24">
        <div className="container mx-auto px-4">
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
            <TabsList className="w-full flex overflow-x-auto gap-2 bg-transparent h-auto p-0 mb-6 pb-2 -mx-4 px-4 md:justify-center md:flex-wrap md:overflow-visible">
              {categories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="px-4 py-3 text-sm font-medium data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground rounded-full border border-border data-[state=active]:border-secondary transition-all whitespace-nowrap flex-shrink-0"
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map((category) => (
              <TabsContent key={category.id} value={category.id} className="mt-0">
                <div className="space-y-6">
                  {category.subcategories.map((subcategory, subIndex) => (
                    <div key={subIndex}>
                      <div className="mb-3">
                        <h3 className="font-serif text-lg md:text-xl font-bold text-primary">
                          {subcategory.name}
                        </h3>
                        {subcategory.note && (
                          <p className="text-xs text-secondary mt-1 font-medium">
                            ✨ {subcategory.note}
                          </p>
                        )}
                      </div>
                      <div className="grid gap-3">
                        {subcategory.items.map((item) => {
                          const quantityInCart = getProductQuantityInCart(item.id);
                          const catalogProduct = getProductById(item.id);
                          const hasCustomization = catalogProduct?.customizationGroups && catalogProduct.customizationGroups.length > 0;
                          const productAvailable = isProductAvailable(item.id);
                          
                          return (
                            <Card
                              key={item.id}
                              className={`bg-card border-border transition-all ${
                                !productAvailable ? "opacity-60 grayscale" : ""
                              } ${
                                !isEscaparateMode && productAvailable ? "cursor-pointer hover:shadow-md active:scale-[0.99]" : ""
                              } ${quantityInCart > 0 ? "ring-2 ring-secondary" : ""}`}
                              onClick={() => handleProductClick(item.id)}
                            >
                              <CardContent className="p-4">
                                <div className="flex justify-between items-center gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className={`font-semibold text-sm md:text-base leading-tight ${
                                        !productAvailable ? "text-muted-foreground line-through" : "text-primary"
                                      }`}>
                                        {item.name}
                                      </h4>
                                      {!productAvailable && (
                                        <Badge variant="destructive" className="text-xs gap-1">
                                          <AlertCircle className="h-3 w-3" />
                                          AGOTADO
                                        </Badge>
                                      )}
                                      {productAvailable && hasCustomization && (
                                        <Badge variant="secondary" className="text-xs">
                                          Personalizable
                                        </Badge>
                                      )}
                                    </div>
                                    {item.tags && item.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {item.tags.map((tag, tagIndex) => (
                                          <TagBadge key={tagIndex} tag={tag} />
                                        ))}
                                      </div>
                                    )}
                                    <span className={`font-bold text-base md:text-lg mt-1 block ${
                                      !productAvailable ? "text-muted-foreground" : "text-secondary"
                                    }`}>
                                      {item.priceDisplay}
                                    </span>
                                  </div>
                                  
                                  {/* Action button or quantity badge */}
                                  {!productAvailable ? (
                                    <Badge variant="outline" className="text-muted-foreground border-muted">
                                      No disponible
                                    </Badge>
                                  ) : isEscaparateMode ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs gap-1 text-muted-foreground"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Camera className="h-3 w-3" />
                                      Escanea QR
                                    </Button>
                                  ) : quantityInCart > 0 ? (
                                    <Badge className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold bg-secondary text-secondary-foreground">
                                      {quantityInCart}
                                    </Badge>
                                  ) : (
                                    <Button
                                      size="icon"
                                      variant="secondary"
                                      className="h-10 w-10 rounded-full"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProductClick(item.id);
                                      }}
                                    >
                                      <Plus className="h-5 w-5" />
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Info Banner */}
      <section className="bg-accent py-6 fixed bottom-16 left-0 right-0 md:relative md:bottom-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="text-accent-foreground text-sm">
            ¿Quieres hacer un encargo especial?{" "}
            <a
              href="/encargos"
              className="font-semibold text-secondary hover:underline"
            >
              Haz tu pedido aquí →
            </a>
          </p>
        </div>
      </section>

      {/* Only show FloatingCart if valid session */}
      {isValidSession && <FloatingCart />}
      
      <LocationSelectorModal
        open={showLocationModal}
        onOpenChange={setShowLocationModal}
        onConfirm={() => setShowLocationModal(false)}
      />

      {/* Product Detail Modal */}
      <ProductDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={selectedProduct}
        existingQuantity={getProductQuantityInCart(selectedProduct?.id)}
        existingOptions={getProductOptionsInCart(selectedProduct?.id)}
        onAddToCart={handleAddFromModal}
        onRemoveFromCart={handleRemoveFromCart}
      />
    </Layout>
  );
};

export default Carta;
